'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import PhotoLightbox from './PhotoLightbox';
import MobileMenu from './MobileMenu';

interface Photo {
  id: string;
  storagePath: string;
  thumbnailPath: string;
  originalName: string;
  width: number;
  height: number;
}

interface Gallery {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  createdAt: string;
  photos: Photo[];
}

interface GalleryDetailProps {
  slug: string;
  siteLogo: string;
  initialGallery: Gallery;
}

export default function GalleryDetail({ siteLogo, initialGallery }: GalleryDetailProps) {
  const [gallery, setGallery] = useState<Gallery | null>(initialGallery);
  const loading = false;
  const error: string | null = null;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    setGallery(initialGallery);
  }, [initialGallery]);

  const openLightbox = (index: number) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToNext = () => {
    if (gallery && currentPhotoIndex < gallery.photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    }
  };

  const goToPrev = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-zinc-950 text-zinc-100 font-sans">
        <header className="fixed top-0 w-full z-50 bg-zinc-950/90 backdrop-blur-md border-b border-white/10 shadow-lg">
          <div className="max-w-[1400px] w-full mx-auto px-4 md:px-8 pt-8 pb-5 flex justify-between items-center">
            <Link href="/" className="group flex items-center gap-6">
              {siteLogo ? (
                <img src={siteLogo} alt="Logo" className="w-16 h-16 rounded-full border-2 border-yellow-500 object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-zinc-900 border-2 border-yellow-500 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  <span className="text-xs text-yellow-500 font-bold">LOGO</span>
                </div>
              )}
              <div className="flex flex-col leading-none uppercase">
                <span className="text-xs font-bold text-yellow-500 tracking-[0.3em]">Pat O&apos;s</span>
                <span className="text-3xl md:text-4xl font-black tracking-tighter text-white">BUNKHOUSE</span>
              </div>
            </Link>
          </div>
        </header>
        <div style={{ height: '100px', minHeight: '100px', flexShrink: 0 }}></div>
        <section className="w-full py-16 flex flex-col items-center">
          <div className="animate-pulse">
            <div className="h-8 bg-zinc-800 rounded w-64 mx-auto mb-4" />
            <div className="h-4 bg-zinc-800 rounded w-32 mx-auto" />
          </div>
        </section>
      </main>
    );
  }

  if (error || !gallery) {
    return (
      <main className="min-h-screen bg-zinc-950 text-zinc-100 font-sans">
        <header className="fixed top-0 w-full z-50 bg-zinc-950/90 backdrop-blur-md border-b border-white/10 shadow-lg">
          <div className="max-w-[1400px] w-full mx-auto px-4 md:px-8 pt-8 pb-5 flex justify-between items-center">
            <Link href="/" className="group flex items-center gap-6">
              {siteLogo ? (
                <img src={siteLogo} alt="Logo" className="w-16 h-16 rounded-full border-2 border-yellow-500 object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-zinc-900 border-2 border-yellow-500 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  <span className="text-xs text-yellow-500 font-bold">LOGO</span>
                </div>
              )}
              <div className="flex flex-col leading-none uppercase">
                <span className="text-xs font-bold text-yellow-500 tracking-[0.3em]">Pat O&apos;s</span>
                <span className="text-3xl md:text-4xl font-black tracking-tighter text-white">BUNKHOUSE</span>
              </div>
            </Link>
          </div>
        </header>
        <div style={{ height: '100px', minHeight: '100px', flexShrink: 0 }}></div>
        <section className="w-full py-16 flex flex-col items-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Gallery Not Found</h1>
          <Link href="/galleries" className="text-yellow-500 hover:underline">
            ← Back to Galleries
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-zinc-950 text-zinc-100 font-sans">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-zinc-950/90 backdrop-blur-md border-b border-white/10 shadow-lg">
        <div className="max-w-[1400px] w-full mx-auto px-4 md:px-8 pt-8 pb-5 flex justify-between items-center">
          <Link href="/" className="group flex items-center gap-6">
            {siteLogo ? (
              <img src={siteLogo} alt="Logo" className="w-16 h-16 rounded-full border-2 border-yellow-500 object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-zinc-900 border-2 border-yellow-500 overflow-hidden flex-shrink-0 flex items-center justify-center">
                <span className="text-xs text-yellow-500 font-bold">LOGO</span>
              </div>
            )}
            <div className="flex flex-col leading-none uppercase">
              <span className="text-xs font-bold text-yellow-500 tracking-[0.3em] group-hover:text-white transition-colors">Pat O&apos;s</span>
              <span className="text-3xl md:text-4xl font-black tracking-tighter text-white group-hover:text-yellow-500 transition-colors">BUNKHOUSE</span>
            </div>
          </Link>
          <nav className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-widest">
            {['Events', 'Specials', 'Hours', 'Crew'].map((item) => (
              <a
                key={item}
                href={item === 'Galleries' ? '/galleries' : `/#${item.toLowerCase()}`}
                className="hover:text-yellow-500 transition-colors"
              >
                {item}
              </a>
            ))}
            <Link href="/galleries" className="text-yellow-500 transition-colors">Galleries</Link>
          </nav>
          <div className="md:hidden flex flex-col items-center gap-2">
            <a href="/#hours" className="text-yellow-500 font-bold uppercase text-xs border border-yellow-500 rounded-full px-8 py-3 tracking-widest">Visit</a>
            <MobileMenu currentPath="/galleries" />
          </div>
        </div>
      </header>

      {/* Spacer to prevent header overlap */}
      <div style={{ height: '100px', minHeight: '100px', flexShrink: 0 }}></div>
      
      {/* Main Content - Styled like Specials/Hours sections */}
      <section className="w-full py-16 bg-zinc-950 flex flex-col items-center">
        <div className="max-w-[1400px] w-full px-4 md:px-8">
          {/* Centered Card Container */}
          <div id="gallery-container" className="gallery-content-box max-w-5xl mx-auto bg-zinc-950/50 p-10 md:p-14 pt-24 rounded-3xl border border-white/5 shadow-2xl flex flex-col items-center" style={{ paddingTop: '4em !important' }}>
            {/* Back Link */}
            <div className="w-full mb-6">
              <Link
                href="/galleries"
                className="text-zinc-400 hover:text-yellow-500 transition-colors text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2"
              >
                ← Back to Galleries
              </Link>
            </div>

            {/* Gallery Header */}
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter mb-3">
                {gallery.name}
              </h1>
              <p className="text-zinc-400">
                {gallery.photos.length} photo{gallery.photos.length !== 1 ? 's' : ''} • Added{' '}
                {new Date(gallery.createdAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
              {gallery.description && (
                <p className="text-zinc-500 mt-2">{gallery.description}</p>
              )}
            </div>

            {/* Photo Grid */}
            {gallery.photos.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <p>No photos in this gallery yet.</p>
              </div>
            ) : (
              <div className="w-full flex justify-center">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {gallery.photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    onClick={() => openLightbox(index)}
                    className="aspect-square rounded-lg overflow-hidden bg-zinc-800 hover:ring-2 hover:ring-yellow-500 transition-all group"
                  >
                    <img
                      src={photo.thumbnailPath}
                      alt={photo.originalName}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </button>
                ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <PhotoLightbox
        photos={gallery.photos}
        currentIndex={currentPhotoIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        onNext={goToNext}
        onPrev={goToPrev}
      />

      {/* Footer */}
      <footer className="w-full pt-8 pb-12 border-t border-white/5">
        <div className="max-w-[1400px] w-full mx-auto px-4 md:px-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-center md:text-left mb-8 md:mb-0">
            <h4 className="text-2xl font-black uppercase text-white mb-2">Pat O&apos;s Bunkhouse</h4>
            <p className="text-zinc-500 text-sm">4428 N 7th Ave, Phoenix, AZ</p>
          </div>
          <div className="flex items-center gap-8 mb-8 md:mb-0">
            <a
              href="https://www.instagram.com/bunkhousesaloon/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white transition-colors"
              aria-label="Instagram"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/BunkhouseSaloon/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white transition-colors"
              aria-label="Facebook"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </div>
          <div className="text-center md:text-right">
            <p className="text-zinc-600 text-xs uppercase tracking-widest">
              &copy; {new Date().getFullYear()} Pat O&apos;s Bunkhouse.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}
